/**
 * Trishul tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const trishulTattoosPack: ToilePack = {
    slug: 'trishul-tattoos',
    category: 'tattoo',
    name: 'Trishul tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'damaru', label: 'Trishul and damaru', subject: 'a trishul standing upright with a small damaru drum tied below the three points by a cord, and a string of rudraksha beads'},
        {key: 'om', label: 'Trishul and Om', subject: 'a trishul with the Om symbol ॐ set at the base of the three points and a third eye drawn above the centre prong'},
        {key: 'serpent', label: 'Trishul and serpent', subject: 'a trishul with a cobra coiled up its shaft, its hood spread just below the three points'},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'blackwork', label: 'Blackwork', style: 'a blackwork tattoo drawing, heavy solid black masses with the details cut out of them as bare paper', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['Nandi bull', 'red ink', 'chest'],
    directionHint: 'try: Nandi bull',
    seo: {
        title: 'Trishul tattoo designs: damaru, Om and third eye, serpent',
        metaDescription: 'Nine trishul tattoo designs: a trishul with its damaru drum, a trishul with Om and the third eye, and a serpent on the shaft, in three styles.',
        h1: 'Trishul tattoo designs: trishul with damaru, trishul and Om, trishul and serpent',
        tagline: 'Three trishuls, each drawn in fine line, blackwork and dotwork on white paper.',
        intro: 'The trishul is the three-pointed trident of Shiva, worn by his devotees on the forearm, the wrist or the chest. The nine sheets show a trishul with the damaru drum tied below its points, a trishul with Om and the third eye, and a trishul with a cobra on its shaft. Each is drawn as fine line, blackwork and dotwork. Type a different symbol or twist, such as a Nandi bull, and the same styles can be asked for.',
        useCases: [
            'a forearm trishul for a devotee',
            'a chest piece with Om and the third eye',
            'a wrist design with a small trishul',
            'a printed sheet to compare styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine trishul designs: a trishul with a damaru, a trishul with Om and a trishul with a serpent, each in three styles.'},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for taking to a studio or printing.'},
            {q: 'What is the damaru?', a: 'It is the small two-headed drum of Shiva, drawn tied below the three points of the trishul.'},
            {q: 'What is the difference between the styles?', a: 'Fine line is one uniform hairline with little shading. Blackwork uses heavy solid black masses. Dotwork builds the shadows from dots.'},
        ],
    },
}
